<template>
    <v-card class="mx-auto elevation-0" color="#3200E6" :loading="loading > 0">
        <confirm ref="confirm"></confirm>
        <v-container class="controller-header" v-if="userCanView || userCanWrite">
            <v-row>
                <v-col class="controller-header__searchbar" :cols="6" d-flex>
                    <search-bar
                        v-on:search="search"
                        v-on:reset-search="returnHome"
                        :search-mode="searchMode"
                        :disabled="error"
                        ref="search"
                    >
                    </search-bar>
                </v-col>
                <v-col class="controller-header__buttons" :cols="6" d-flex>
                    <v-btn class="controller-header__buttons__button" @click="navigate(path, driveItemId)">
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                    <v-btn class="controller-header__buttons__button" @click="returnHome">
                        <v-icon>mdi-home</v-icon>
                    </v-btn>
                    <v-btn class="controller-header__buttons__button" @click="addFolder" :disabled="error" v-if="userCanWrite">
                        {{ $t('docs.toolbar.actions.folder.text') }}&nbsp;&nbsp;<v-icon
                            class="controller-header__buttons__button__icon"
                            >mdi-folder</v-icon
                        >
                    </v-btn>
                    <v-btn
                        class="controller-header__buttons__button controller-header__buttons__button--reverse"
                        @click="uploadFile"
                        :disabled="error"
                        v-show="userCanWrite"
                    >
                        {{ $t('docs.toolbar.actions.file.text') }}&nbsp;&nbsp;<v-icon
                            class="controller-header__buttons__button__icon"
                            >mdi-plus-circle</v-icon
                        >
                    </v-btn>
                </v-col>
            </v-row>
        </v-container>
        <v-container class="file-explorer--wrapper">
            <v-row>
                <v-col cols="12" class="file-explorer__col">
                    <div class="file-explorer__results" v-if="searchMode">
                        {{ $t('docs.searchbar.results') }}: {{ items.length }} {{ $t('docs.searchbar.files') }}
                    </div>
                    <v-breadcrumbs :items="breadcrumbItems" class="file-explorer__breadcrumb" v-if="!searchMode">
                        <template v-slot:item="{ item }">
                            <v-breadcrumbs-item
                                @click="navigate(item.path, item.driveItemId)"
                                class="file-explorer__breadcrumb__item"
                            >
                                {{ item.label.toUpperCase() }}
                            </v-breadcrumbs-item>
                        </template>
                    </v-breadcrumbs>
                </v-col>
            </v-row>
            <v-row>
                <v-col :cols="selectedItem ? 8 : 12" class="file-explorer__col file-explorer__col--border" d-flex>
                    <v-data-table
                        v-if="!error && (userCanView || userCanWrite)"
                        :headers="searchMode ? headers.search : headers.normal"
                        :items="formattedItems"
                        :items-per-page="10"
                        class="file-explorer elevation-0"
                        :item-class="rowClass"
                        @click:row="setSelected"
                        @dblclick:row="attemptNavigate"
                        :footer-props="{ itemsPerPageOptions: [10, 20, 50, -1] }"
                    >
                        <template v-slot:[`item.view`]="{ item }">
                            <div v-if="item.extension !== 'folder'" :style="!showViewIcon(item) ? 'margin-left: 25px;' : ''">
                                <v-tooltip bottom v-if="showViewIcon(item)">
                                    <template v-slot:activator="{ on, attrs }">
                                        <span class="icon" @click.stop="" v-on="on" :attrs="attrs"
                                            ><a target="_blank" @click.stop="preview(item)" class="icon__link__transparent"
                                                ><v-icon>mdi-eye</v-icon></a
                                            ></span
                                        >
                                    </template>
                                    <span>{{ $t('docs.list.tooltips.preview') }}</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <span class="icon" @click.stop="" v-on="on" :attrs="attrs"
                                            ><a target="_blank" :href="item.download" class="icon__link__transparent"
                                                ><v-icon>mdi-download-outline</v-icon></a
                                            ></span
                                        >
                                    </template>
                                    <span>{{ $t('docs.list.tooltips.download') }}</span>
                                </v-tooltip>
                                <v-tooltip bottom>
                                    <template v-slot:activator="{ on, attrs }">
                                        <span class="icon icon__action" @click.stop="setSelected(item)" v-on="on" :attrs="attrs"
                                            ><v-icon>mdi-information-outline</v-icon></span
                                        >
                                    </template>
                                    <span>{{ $t('docs.list.tooltips.details') }}</span>
                                </v-tooltip>
                            </div>
                        </template>

                        <template v-slot:[`item.edit`]="{ item }">
                            <v-tooltip v-if="item.extension !== 'folder'" bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <span class="icon icon_action" @click.stop="updateFile(item)" v-on="on" :attrs="attrs"
                                        ><v-icon>mdi-history</v-icon></span
                                    >
                                </template>
                                <span>{{ $t('docs.list.tooltips.update') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <span class="icon icon_action" @click.stop="renameItem(item)" v-on="on" :attrs="attrs"
                                        ><v-icon>mdi-pencil-outline</v-icon></span
                                    >
                                </template>
                                <span>{{ $t('docs.list.tooltips.rename') }}</span>
                            </v-tooltip>
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <span class="icon icon_action" @click.stop="deleteFile(item)" v-on="on" :attrs="attrs"
                                        ><v-icon>mdi-delete-outline</v-icon></span
                                    >
                                </template>
                                <span>{{ $t('docs.list.tooltips.delete') }}</span>
                            </v-tooltip>
                        </template>

                        <template v-slot:[`item.name`]="{ item }">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on, attrs }">
                                    <span v-on="on" :attrs="attrs">
                                        <v-icon :color="getIcon(item.extension).color">{{ getIcon(item.extension).icon }}</v-icon>
                                        &nbsp;{{ shortenName(item.name) }}
                                    </span>
                                </template>
                                <span>{{ item.name }}</span>
                            </v-tooltip>
                        </template>
                    </v-data-table>
                    <v-card class="file-explorer__error-wrapper elevation-0" v-if="error || !(userCanView || userCanWrite)">
                        <v-content>
                            <div class="error-panel">
                                <v-icon>mdi-minus-circle</v-icon><br />
                                {{ $t('docs.error.auth') }}
                            </div>
                        </v-content>
                    </v-card>
                </v-col>
                <v-col cols="4" v-if="selectedItem" class="file-explorer__col file-explorer__col--border" d-flex>
                    <v-card class="file-explorer__sidepanel elevation-0">
                        <v-card-title class="file-explorer__sidepanel__title">
                            <v-row>
                                <v-col :cols="11">
                                    <v-icon :color="getIcon(selectedItem.extension).color">{{
                                        getIcon(selectedItem.extension).icon
                                    }}</v-icon>
                                    &nbsp;
                                    {{ selectedItem.name }}
                                </v-col>
                                <v-col :cols="1" class="text-right">
                                    <v-icon @click="resetSelected">mdi-close</v-icon>
                                </v-col>
                            </v-row>
                        </v-card-title>
                        <v-divider></v-divider>
                        <v-card-text class="file-explorer__sidepanel__body elevation-0">
                            <v-expansion-panels multiple v-model="expandedPanels">
                                <v-expansion-panel class="file-explorer__sidepanel__body__panel">
                                    <v-expansion-panel-header class="file-explorer__sidepanel__body__panel__title">
                                        {{ $t('docs.informations.title') }}
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content class="file-explorer__sidepanel__body__panel__content">
                                        <side-panel-table :display-data="detailsTableItems"></side-panel-table>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel class="file-explorer__sidepanel__body__panel">
                                    <v-expansion-panel-header class="file-explorer__sidepanel__body__panel__title">
                                        {{ $t('docs.informations.metadata') }}
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content class="file-explorer__sidepanel__body__panel__content">
                                        <side-panel-table :display-data="metadataTableItems"></side-panel-table>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                                <v-expansion-panel class="file-explorer__sidepanel__body__panel">
                                    <v-expansion-panel-header class="file-explorer__sidepanel__body__panel__title">
                                        {{ $t('docs.informations.versions') }}
                                    </v-expansion-panel-header>
                                    <v-expansion-panel-content class="file-explorer__sidepanel__body__panel__content">
                                        <side-panel-table :display-data="versionsTableItems"></side-panel-table>
                                    </v-expansion-panel-content>
                                </v-expansion-panel>
                            </v-expansion-panels>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
        <v-dialog v-model="isRenaming" max-width="460">
            <v-card class="dialog__rename-item">
                <v-card-title>{{ $t('docs.rename.title') }}</v-card-title>
                <v-card-subtitle>{{ $t('docs.rename.subtitle') }}</v-card-subtitle>
                <v-card-text>
                    <v-text-field
                        color="#3200E6"
                        :label="$t('docs.rename.label')"
                        v-model="newItemName"
                        hide-details
                    ></v-text-field>
                    <v-btn class="action" :disabled="!newItemName" @click="confirmRenameItem">
                        {{ $t('docs.rename.rename') }}
                    </v-btn>
                    <v-btn @click="isRenaming = false" class="secondary-action">{{ $t('docs.rename.cancel') }}</v-btn>
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="isMkdir" max-width="460">
            <v-card class="dialog__mkdir">
                <v-card-title>{{ $t('docs.toolbar.actions.folder.text') }}</v-card-title>
                <v-card-subtitle>{{ $t('docs.toolbar.actions.folder.tooltip') }}</v-card-subtitle>
                <v-card-text>
                    <v-text-field
                        color="#3200E6"
                        :label="$t('docs.toolbar.actions.folder.name')"
                        v-model="newFolderName"
                        hide-details
                    ></v-text-field>
                    <v-btn class="action" :disabled="!newFolderName" @click="confirmMkdir">
                        {{ $t('docs.toolbar.actions.folder.add') }}
                    </v-btn>
                    <v-btn @click="isMkdir = false" class="secondary-action">{{ $t('docs.toolbar.actions.folder.cancel') }}</v-btn>
                </v-card-text>
            </v-card>
        </v-dialog>
        <v-dialog v-model="showUpload" max-width="500">
            <upload
                :replacing="isReplacing"
                :path="path"
                :client="client"
                :driveItemId="driveItemId"
                :storage="storage"
                :files="uploadingFiles"
                :maxUploadFilesCount="isReplacing ? 1 : maxUploadFilesCount"
                :maxUploadFileSize="maxUploadFileSize"
                :nameToMatch="nameToMatch"
                v-on:cancel="showUpload = false"
                v-on:uploaded="uploaded"
                v-on:errored="errorUpload = $event"
            />
        </v-dialog>
        <v-dialog v-model="showErrorUpload" max-width="500px">
            <v-card>
                <p class="p-docs">
                    {{ errorUpload }}
                    <br />
                    {{ $t('docs.upload.errorUpload') }}
                </p>
                <v-card-actions>
                    <v-btn color="primary" text @click="errorUpload = null">
                        {{ $t('forms.errors.close') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import { api } from '@/utils/api';
import { ROOT_DIR, MAX_NAME_LENGTH, getIcon } from '../../utils/file-icon';
import { mapGetters } from 'vuex';
import { formatBytes } from './util';
import Confirm from './Confirm';
import Upload from './Upload';
import SearchBar from './SearchBar';
import SidePanelTable from './SidePanelTable';
import moment from 'moment';

export default {
    model: {
        prop: 'path',
        event: 'change'
    },
    components: { Confirm, Upload, SearchBar, SidePanelTable },
    props: {
        // code of default storage
        storage: { type: String, default: 'sharepoint' },
        // max files count to upload at once. Unlimited by default
        maxUploadFilesCount: { type: Number, default: 0 },
        // max file size to upload. Unlimited by default
        maxUploadFileSize: { type: Number, default: 0 }
    },
    data() {
        return {
            loading: 0, // tells if data are loading
            selectedItem: null, // selected item currently displayed in side panel
            items: [], // list of items in the current directory
            path: null, // path of the current directory
            driveItemId: '', // driveItemId of the current directory
            uploadingFiles: false, // or an Array of files
            isRenaming: false, // is a renaming operation ongoing?
            isMkdir: false, // is a mkDir operation ongoing?
            newItemName: '', // new item name in the renaming dialog
            newFolderName: '', // new folder name in the mkdir dialog
            nameToMatch: '', // name expected to match in the update file dialog
            showUpload: false, // is an upload operation ongoing?
            isReplacing: false, // is an update operation ongoing? Note: an update is also an upload
            expandedPanels: [0], // id of the side panel section to expand at start (default Informations)
            searchMode: false, // is a search operation ongoing?
            error: false, // did the back send an error? (considered as an access error)
            errorUpload: null, // did the back send an error? (considered as an access error)
            knownDriveIds: [] // list of known driveItemId for breadcrumb navigation
        };
    },
    mounted() {
        this.returnHome();
    },
    watch: {
        errorUpload(evt) {
            console.log(evt);
        },
        '$store.getters.getCurrentClientId'() {
            if (this.$store.getters.getCurrentClientId && !this.path) {
                this.navigate(ROOT_DIR);
            }
        }
    },
    computed: {
        ...mapGetters({
            client: 'getClientName',
            accessRoles: 'getCurrentDocsPortletAccess'
        }),

        userCanView() {
            return this.accessRoles?.viewer;
        },
        userCanWrite() {
            return this.accessRoles?.writer;
        },
        showErrorUpload() {
            return !!this.errorUpload;
        },
        /**
         * Returns the array of header cells to display. Different if a search is going on.
         * @returns {Array}
         */
        headers() {
            return {
                normal: [
                    { text: this.$t('docs.list.headers.name'), value: 'name' },
                    { text: this.$t('docs.list.headers.type'), value: 'type' },
                    { text: this.$t('docs.list.headers.creation_date'), value: 'publicationDate' },
                    { text: this.$t('docs.list.headers.modified_date'), value: 'modifiedDate' },
                    { text: this.$t('docs.list.headers.view'), value: 'view', align: 'center' },
                    ...(this.userCanWrite ? [{ text: this.$t('docs.list.headers.edit'), value: 'edit', align: 'right' }] : [])
                ],
                search: [
                    { text: this.$t('docs.list.headers.name'), value: 'name' },
                    { text: this.$t('docs.list.headers.type'), value: 'type' },
                    { text: this.$t('docs.list.headers.path'), value: 'path' },
                    { text: this.$t('docs.list.headers.creation_date'), value: 'publicationDate' },
                    { text: this.$t('docs.list.headers.modified_date'), value: 'modifiedDate' },
                    { text: this.$t('docs.list.headers.view'), value: 'view', align: 'center' },
                    ...(this.userCanWrite ? [{ text: this.$t('docs.list.headers.edit'), value: 'edit', align: 'right' }] : [])
                ]
            };
        },
        /**
         * Enriched items to display in the data table
         * TODO: Refactor to make use of formatted/original items consistent
         * @returns {Object}
         */
        formattedItems() {
            const formatted = this.items?.map(entry => {
                return {
                    type: entry.type === 'file' ? entry.extension.replace('.', '').toUpperCase() : this.$t('docs.list.folder'),
                    name: entry.name,
                    path: entry.path,
                    publicationDate: this.convertDateUtcToLocal(entry.createdDateTime),
                    modifiedDate: this.convertDateUtcToLocal(entry.lastModified),
                    download: entry.downloadUrl,
                    driveItemId: entry.driveItemId,
                    extension: entry.type === 'file' ? entry.extension.replace('.', '').toLowerCase() : 'folder',
                    size: formatBytes(entry.size),
                    author: entry.author,
                    mimeType: entry.mimeType
                };
            });

            return formatted;
        },
        /**
         * List of breadcrumb items to display
         * @returns {Array}
         */
        breadcrumbItems() {
            const rootItem = {
                path: ROOT_DIR,
                label: 'Root',
                driveItemId: ''
            };

            if (!this.path || this.path === ROOT_DIR) return [rootItem];

            const labelsArray = this.path.split('/').filter(subpath => !!subpath);
            const breadcrumbItems = [rootItem];
            let currPath = rootItem.path;

            for (const label of labelsArray) {
                currPath = `${currPath}${label}/`;
                const { driveItemId } = this.knownDriveIds.find(item => item.path === currPath);
                const newItem = {
                    path: currPath,
                    driveItemId,
                    label
                };
                breadcrumbItems.push(newItem);
            }

            return breadcrumbItems;
        },
        /**
         * Items table to send to the details section of the side panel
         * @returns {Array}
         */
        detailsTableItems() {
            if (!this.selectedItem) return [];
            return [
                { label: this.$t('docs.informations.type'), value: this.selectedItem.extension },
                { label: this.$t('docs.informations.size'), value: this.selectedItem.size },
                { label: this.$t('docs.informations.path'), value: this.selectedItem.path },
                { label: this.$t('docs.informations.updated'), value: this.selectedItem.modifiedDate },
                { label: this.$t('docs.informations.added'), value: this.selectedItem.publicationDate }
            ];
        },
        /**
         * Items table to send to the versions section of the side panel
         * @returns {Array}
         */
        versionsTableItems() {
            if (!this.selectedItem) return [];

            const versions = [];

            if (!(this.selectedItem.versions && this.selectedItem.versions.length)) {
                versions.push({ label: `${this.$t('docs.informations.version')} 1.0`, value: this.selectedItem.publicationDate });
            } else {
                for (const version of this.selectedItem.versions) {
                    versions.push({
                        label: `${this.$t('docs.informations.version')} ${version.id}`,
                        value: version.lastModifiedDateTime
                    });
                }
            }

            return versions;
        },
        /**
         * Items table to send to the metadata section of the side panel
         * @returns {Array}
         */
        metadataTableItems() {
            if (!this.selectedItem || !this.selectedItem.metadata) return [];

            const metadata = [];

            for (const [key, value] of this.selectedItem.metadata) {
                metadata.push({ label: key, value });
            }

            return metadata;
        }
    },
    methods: {
        getIcon,
        /**
         * Updates the loading data when an API call starts or has a return value
         * @param {boolean} loading
         */
        loadingChanged(loading) {
            if (loading) {
                this.loading++;
            } else if (this.loading > 0) {
                this.loading--;
            }
        },
        /**
         * Getter for the icon matching the file type of an item
         * @param {Object} item
         * @returns {string}
         */
        rowClass(item) {
            const isSelected = this.selectedItem?.driveItemId === item.driveItemId;
            return isSelected ? 'file-explorer__row file-explorer__row--selected' : 'file-explorer__row';
        },
        /**
         * Triggers when an upload operation is completed
         */
        uploaded() {
            this.uploadingFiles = false;
            this.showUpload = false;
            this.navigate(this.path, this.driveItemId);
        },
        /**
         * Triggered when creating a new dir
         */
        addFolder() {
            this.isMkdir = true;
        },
        /**
         * Triggered when uploading a new file
         */
        uploadFile() {
            this.showUpload = true;
            this.isReplacing = false;
            this.nameToMatch = '';
        },
        /**
         * Triggered when updating a new file
         * @param {Object} item File item being updated
         */
        updateFile(item) {
            this.showUpload = true;
            this.isReplacing = true;
            this.nameToMatch = item.name;
        },
        /** Triggered when renaming a file
         * @param {Object} item File being renamed
         */
        renameItem(item) {
            this.isRenaming = true;
            this.selectedItem = item;
        },
        /**
         * Triggered after confirming renaming a file
         */
        async confirmRenameItem() {
            const requestBody = {
                client: this.client,
                name: this.newItemName,
                driveItemId: this.selectedItem.driveItemId,
                extension:
                    this.selectedItem.extension && this.selectedItem.extension !== 'folder'
                        ? `.${this.selectedItem.extension}`
                        : null
            };
            try {
                await api.post(['sharepoint', 'rename'], null, {
                    ...requestBody,
                    clientId: this.$store.getters.getCurrentClientId,
                    portletId: this.$store.getters.getCurrentDocsPortlet
                });

                this.navigate(this.path, this.driveItemId);
            } catch (error) {
                this.$store.commit('displayErrors', { errno: 4405 });
                console.error(error);
            } finally {
                this.isRenaming = false;
                this.newItemName = '';
            }
        },
        /**
         * Triggered after confirming a mkdir operation
         */
        async confirmMkdir() {
            const requestBody = {
                client: this.client,
                path: this.path,
                dirName: this.newFolderName,
                driveItemId: this.driveItemId || ''
            };
            try {
                await api.post(['sharepoint', 'mkdir'], null, {
                    ...requestBody,
                    clientId: this.$store.getters.getCurrentClientId,
                    portletId: this.$store.getters.getCurrentDocsPortlet
                });
                this.isMkdir = false;
                this.newFolderName = '';
                this.navigate(this.path, this.driveItemId);
            } catch (error) {
                this.isMkdir = false;
                this.newFolderName = '';
                this.$store.commit('displayErrors', { errno: 4405 });
                console.error(error);
            }
        },
        /** Triggered when deleting a file
         * @param {Object} item File being deleting
         */
        async deleteFile(item) {
            const confirmed = await this.$refs.confirm.open(
                this.$t('docs.delete.title'),
                this.$t(`${item.type === 'dir' ? 'docs.delete.folder' : 'docs.delete.file'}`, { name: item.name })
            );
            if (confirmed) {
                const requestConfig = {
                    params: {
                        client: this.client,
                        driveItemId: item.driveItemId,
                        clientId: this.$store.getters.getCurrentClientId,
                        portletId: this.$store.getters.getCurrentDocsPortlet
                    }
                };
                try {
                    const result = await api.delete('delete', null, requestConfig, 'sharepoint');
                    this.navigate(this.path, this.driveItemId);
                    if (!result) {
                        throw Error('Unauthorized');
                    }
                } catch (error) {
                    this.$store.commit('displayErrors', { errno: 4405 });
                    console.error(error);
                }
            }
        },
        /** Sets an item as selected and retrives the item informations
         * @param {Object} item selectedItem to set
         */
        async setSelected(item) {
            this.$refs.search.reset();
            if (item.extension !== 'folder') {
                this.loadingChanged(true);
                const requestConfig = {
                    params: {
                        client: this.client,
                        path: this.path,
                        driveItemId: item.driveItemId,
                        fileName: item.name
                    }
                };

                try {
                    const remoteItem = await api.get(
                        'info',
                        { clientId: this.$store.getters.getCurrentClientId, portletId: this.$store.getters.getCurrentDocsPortlet },
                        requestConfig,
                        'sharepoint'
                    );

                    let fileInfo = this.changeInfoObjectDates({
                        ...item,
                        versions: remoteItem.versions,
                        metadata: remoteItem.tags
                    });
                    this.selectedItem = fileInfo;
                    this.expandedPanels = [0];
                    this.loadingChanged(false);
                } catch (e) {
                    console.error(e);
                    this.$store.commit('displayErrors', { errno: 4405 });
                    this.selectedItem = null;
                    this.loadingChanged(false);
                }
            } else {
                this.selectedItem = null;
            }
        },

        showViewIcon(file) {
            const fileType = file?.mimeType;
            return fileType.startsWith('image/') || fileType === 'application/pdf' || fileType.startsWith('text/');
        },
        preview(file) {
            const fileUrl = file.download;

            // Open the file in a new tab
            const newTab = window.open();

            // Check if the browser blocks the new tab creation
            if (!newTab) {
                alert('Popup blocked! Please allow popups for this website.');
                return;
            }

            // Display a temporary loading message in the new tab
            newTab.document.write('<h3>Loading file, please wait...</h3>');

            // Fetch the file and show it in the new tab
            fetch(fileUrl)
                .then(response => response.blob())
                .then(blob => {
                    const fileType = blob.type;
                    const fileBlobUrl = URL.createObjectURL(blob);

                    // Handle supported file types
                    if (fileType.startsWith('image/') || fileType === 'application/pdf' || fileType.startsWith('text/')) {
                        // Replace the loading message with the file
                        newTab.location.href = fileBlobUrl; // Directly show the file in the new tab
                    } else {
                        // For unsupported file types, provide a download option
                        newTab.location.href = fileBlobUrl;
                        newTab.document.write(
                            '<h3>This file type cannot be displayed in the browser. It has been downloaded instead.</h3>'
                        );
                    }
                })
                .catch(error => {
                    console.error('Error fetching the file:', error);

                    // Replace the loading message with an error message
                    newTab.document.body.innerHTML = '<h3>Failed to load the file. Please try again later.</h3>';
                });
        },
        /**
         * Resets selected Item and closes sidepanel
         */
        resetSelected() {
            this.selectedItem = null;
        },
        /**
         * Triggers to attempt a navigate event if the target is a folder
         * @param {MouseEvent} event The onClick event (not used but mandatory first param)
         * @param {Object} dataWrapper The item object wrapper the navigate attempt is made on
         */
        attemptNavigate(event, dataWrapper) {
            const { item } = dataWrapper;
            if (item.extension === 'folder' && item.path) {
                this.navigate(item.path, item.driveItemId);
            }
        },
        /**
         * Forces the return to the Root element
         */
        returnHome() {
            this.searchMode = false;
            this.$refs.search.reset();
            this.navigate(ROOT_DIR);
        },
        /**
         * Navigates to selected path
         * @param {string} path
         * @param {string} driveItemId
         */
        async navigate(path, driveItemId) {
            this.selectedItem = null;
            this.loadingChanged(true);

            const requestConfig = {
                params: {
                    client: this.client,
                    path,
                    driveItemId
                }
            };

            try {
                this.items = await api.get(
                    'list',
                    {
                        clientId: this.$store.getters.getCurrentClientId,
                        portletId: this.$store.getters.getCurrentDocsPortlet
                    },
                    requestConfig,
                    'sharepoint'
                );
                if (!this.items.error) {
                    this.path = path;
                    this.driveItemId = driveItemId;
                    this.registerDriveItemsId(this.path, this.driveItemId);
                    this.error = false;
                } else {
                    this.error = true;
                }
            } catch (e) {
                console.error(e);
                this.error = true;
            }
            this.loadingChanged(false);
        },
        /**
         * Performs a search based on the searchbar component
         * @param {string} queryString
         */
        async search(queryString) {
            if (!queryString || queryString.length < 3) return;
            this.loadingChanged(true);

            const requestConfig = {
                params: {
                    client: this.client,
                    path: this.path,
                    driveItemId: this.driveItemId,
                    search: queryString
                }
            };

            try {
                this.items = await api.get(
                    'search',
                    {
                        clientId: this.$store.getters.getCurrentClientId,
                        portletId: this.$store.getters.getCurrentDocsPortlet
                    },
                    requestConfig,
                    'sharepoint'
                );
                this.path = ROOT_DIR;
                this.searchMode = true;
                this.loadingChanged(false);
            } catch (err) {
                console.error(err);
                this.$store.commit('displayErrors', { errno: 4405 });
                this.loadingChanged(false);
            }
        },
        /**
         * Add a new driveItemId/path pair for use in the breadcrumb
         * @param {string} path
         * @param {string} driveItemId
         */
        registerDriveItemsId(path, driveItemId) {
            if (path && driveItemId) {
                this.knownDriveIds.push({ path, driveItemId });
            }
        },
        /**
         * Returns formatted file name based on MAX_NAME_LENGTH value
         * @param {string} name
         * @returns {string}
         */
        shortenName(name) {
            return name.length < MAX_NAME_LENGTH ? name : `${name.slice(0, MAX_NAME_LENGTH)}...`;
        },
        /**
         * Returns formatted local date
         * @param {string} date
         * @returns {string}
         */
        convertDateUtcToLocal(date) {
            return date ? moment(new Date(date)).format('YYYY-MM-DD HH:mm:ss') : '';
        },
        //map all object and detect dates
        changeInfoObjectDates(infoDoc) {
            let object = infoDoc;
            const regexDate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;

            if (object && Object.keys(object).length !== 0) {
                for (let key in object) {
                    if (!Array.isArray(object[key]) && typeof object[key] !== 'object') {
                        if (moment(object[key]).isValid()) {
                            object[key] = this.convertDateUtcToLocal(object[key]);
                        }
                    } else if (Array.isArray(object[key])) {
                        let subObject = object[key];
                        for (let index in subObject) {
                            if (Array.isArray(subObject[index])) {
                                //is Metadata
                                if (subObject[index].length === 2) {
                                    subObject[index][1] =
                                        !Array.isArray(subObject[index][1]) && regexDate.test(subObject[index][1])
                                            ? this.convertDateUtcToLocal(subObject[index][1])
                                            : subObject[index][1];
                                }
                            } else if (typeof subObject[index] === 'object') {
                                //is version
                                subObject[index] = regexDate.test(subObject[index])
                                    ? this.changeInfoObjectDates(subObject[index])
                                    : subObject[index];
                            }
                        }
                        object[key] = subObject;
                    }
                }
            }
            return object;
        }
    }
};
</script>

<style lang="scss" scoped>
.p-docs {
    padding: 20px 20px 0;
}
.col {
    background-color: #fff;
}

.file-explorer {
    &--wrapper {
        padding: 0;
    }

    &__error-wrapper {
        min-height: 568px;
        padding-top: 200px;
        text-align: center;
    }

    border: none;
    border-radius: 0px;

    ::v-deep &__row {
        cursor: pointer !important;
        user-select: none;

        &--selected {
            background-color: lightgray !important;
        }
    }

    &__col {
        padding: 0;
        box-shadow: none !important;

        &--border {
            // border: 1px solid lightgrey;
            min-height: 568px;
        }
    }

    &__breadcrumb {
        background-color: white;
        min-width: 100%;

        &__item {
            user-select: none;
            cursor: pointer;
            font-weight: bold;
            color: #3200e6;
        }
    }

    &__sidepanel {
        background-color: white;
        border: none;
        border-radius: 0px !important;
        box-shadow: none;

        &__title {
            font-family: 'IBM Plex Mono', monospace;
            color: black;
            font-size: 12px;
            font-weight: normal;
        }

        &__body {
            padding: 0;
            border: none;
            border-radius: 0px;

            &__panel {
                margin-top: 0px !important;

                &__title {
                    font-family: 'IBM Plex Sans', monospace;
                    font-size: 12px;
                    color: #818180;
                    text-transform: uppercase;
                }

                &__content {
                    font-family: 'IBM Plex Mono', monospace;
                    font-size: 12px;
                    color: #000000;
                }
            }
        }
    }
}

.controller-header {
    padding: 0px;

    &__buttons {
        text-align: right;

        &__button {
            margin: 6px;
            font-family: 'IBM Plex Sans', monospace;
            font-size: 16px;
            text-transform: none;
            text-indent: 0px;
            letter-spacing: 0em;
            font-weight: bold;
            padding-left: 16px !important;
            padding-right: 16px !important;

            &__icon {
                margin-left: 6px;
            }

            &--reverse {
                background-color: #3200e6 !important;
                color: white;
            }
        }
    }

    ::v-deep &__searchbar {
        &__button {
            margin: 6px;
        }
    }
}

.icon {
    &__action {
        cursor: pointer;
    }
    &__link {
        &__transparent {
            color: transparent;
        }
    }
}

.dialog {
    &__rename-item,
    &__mkdir {
        .v-card__subtitle {
            padding-bottom: 0;
            margin-top: 0;
        }
        .v-card__text {
            .v-input {
                margin-bottom: 20px;
            }
            button {
                margin-right: 16px;
            }
        }
    }
}
</style>
